package com.swetdot.swetgram.data.auth

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.toDomain
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.protocol.dto.AuthSessionResponse
import com.swetdot.swetgram.protocol.dto.ForgotPasswordRequest
import com.swetdot.swetgram.protocol.dto.LoginRequest
import com.swetdot.swetgram.protocol.dto.LogoutRequest
import com.swetdot.swetgram.protocol.dto.RefreshRequest
import com.swetdot.swetgram.protocol.dto.RegisterRequest
import com.swetdot.swetgram.protocol.dto.ResetPasswordRequest
import com.swetdot.swetgram.protocol.dto.UserPrivate
import com.swetdot.swetgram.protocol.dto.UserPublic
import com.swetdot.swetgram.protocol.dto.VerifyEmailRequest
import com.swetdot.swetgram.protocol.dto.LegalContract
import io.ktor.client.request.setBody

/**
 * The authentication surface of the contract, expressed in the client's own terms.
 *
 * Every call that returns a session adopts it immediately, so a caller cannot forget to store
 * the tokens and end up with a "successful" login that leaves the app signed out.
 */
class AuthRepository(
    private val api: ApiClient,
    private val session: SessionManager,
    /**
     * Run after the local session is dropped, to discard everything that session cached.
     *
     * A hook rather than a cache reference so this class keeps knowing nothing about SQLite and
     * stays constructible in a test with two fakes. Defaulted to a no-op for those tests; the
     * composition root supplies the real one, and wires the SAME lambda into the revoked-session
     * path, because a session can end without anybody tapping Sign Out.
     */
    private val onSignedOut: suspend () -> Unit = {},
) {
    suspend fun register(
        displayName: String,
        username: String,
        email: String,
        password: String,
        acceptedLegal: Boolean,
        ageConfirmed: Boolean,
    ): User {
        val response: AuthSessionResponse = api.post("auth/register", authenticated = false) {
            setBody(
                RegisterRequest(
                    displayName = displayName.trim(),
                    // The contract's Username is lowercase-normalised; sending mixed case
                    // would be rejected by its pattern.
                    username = username.trim().lowercase(),
                    email = email.trim(),
                    password = password,
                    acceptedTermsVersion = LegalContract.CURRENT_VERSION,
                    acceptedPrivacyVersion = LegalContract.CURRENT_VERSION,
                    ageConfirmed = ageConfirmed && acceptedLegal,
                ),
            )
        }
        session.adopt(response)
        return response.user.toUser()
    }

    /** `identifier` accepts either a username or an email address. */
    suspend fun login(identifier: String, password: String): User {
        val response: AuthSessionResponse = api.post("auth/login", authenticated = false) {
            setBody(LoginRequest(identifier = identifier.trim(), password = password))
        }
        session.adopt(response)
        return response.user.toUser()
    }

    /** The code from the verification email. 204 on success. */
    suspend fun verifyEmail(code: String) {
        api.post<Unit>("auth/verify-email", authenticated = false) { setBody(VerifyEmailRequest(token = code.trim())) }
    }

    suspend fun resendVerification() {
        api.post<Unit>("auth/resend-verification")
    }

    /**
     * Step one of password recovery. 202, always.
     *
     * `authenticated = false` is not an optimisation here, it is the difference between a working
     * app and one that stops after fifteen minutes: the contract declares `security: []` on this
     * operation, and asking [ApiClient] to authenticate means asking the token provider for a
     * token, which re-enters [SessionManager]'s refresh mutex and hangs the caller with no request
     * and no exception. See the comment on [ApiClient.post].
     *
     * It returns Unit and there is nothing else it could return. The server answers `202` with an
     * EMPTY body whether or not the address belongs to an account -- confirmed against the
     * deployed server for a registered address, an unregistered one and a malformed one, all
     * three byte-identical. Callers must not try to infer existence from it, and the screens
     * above must not word themselves as if they had.
     *
     * Note the caveat, because it is not this layer's to fix: the deployed server sends the mail
     * INLINE before responding, so the registered address answered in ~2.9s and the unregistered
     * one in ~68ms. The body carries no signal; the clock does.
     *
     * The address is only trimmed: the server matches on `lower(u.email) = lower(?)`, so case is
     * already its problem and lowercasing here would merely hide what the user typed.
     */
    suspend fun forgotPassword(email: String) {
        api.post<Unit>("auth/forgot-password", authenticated = false) { setBody(ForgotPasswordRequest(email = email.trim())) }
    }

    /**
     * Step two. 204 on success -- and NO session: the contract returns no body, and the server
     * revokes every session the account had, so the user must sign in again afterwards.
     *
     * `authenticated = false` for the same reason as [forgotPassword], and more pointedly: the
     * caller of this one is by definition someone who could not sign in, so there is no token to
     * attach even in principle.
     *
     * The two failures both arrive as `400 validation_failed` naming a field -- `token` for a
     * code that is wrong, spent or past its 15 minutes, `new_password` for one the contract's
     * `Constraints` reject.
     */
    suspend fun resetPassword(code: String, newPassword: String) {
        api.post<Unit>("auth/reset-password", authenticated = false) {
            setBody(ResetPasswordRequest(token = code.trim(), newPassword = newPassword))
        }
    }

    /**
     * Ends the session on this device. The local session is cleared even if the network call
     * fails: a user who taps Sign Out must end up signed out, and leaving them logged in
     * because a request timed out would be both surprising and a privacy problem on a shared
     * device. The server-side token expires on its own.
     *
     * The cached inbox goes with it, for the same reason and by the same rule -- see
     * [onSignedOut]. Dropping the tokens alone would leave the previous account's conversations
     * on disk to be rendered for whoever signs in next.
     */
    suspend fun logout() {
        val refreshToken = session.state.value?.refreshToken
        try {
            if (refreshToken != null) {
                api.post<Unit>("auth/logout") { setBody(LogoutRequest(refreshToken = refreshToken)) }
            }
        } catch (_: ApiError) {
            // Deliberately swallowed; the local sign-out below is what the user asked for.
        } finally {
            session.signOut()
            // Swallowed too, and deliberately AFTER the session is gone: the user is signed out
            // either way, and a failed delete must not be reported as a failed sign-out. The next
            // sign-in clears it again.
            runCatching { onSignedOut() }
        }
    }

    /**
     * Exchanges the refresh token. Passed to [SessionManager.refreshIfNeeded] rather than
     * called directly, so concurrent callers cannot each spend the rotating token -- the second
     * one would trip `refresh_token_reuse_detected` and get the whole session revoked.
     */
    suspend fun exchangeRefreshToken(refreshToken: String): AuthSessionResponse =
        api.post("auth/refresh", authenticated = false) { setBody(RefreshRequest(refreshToken = refreshToken)) }

    suspend fun me(): User = api.get<UserPrivate>("users/me").toUser()
}

/**
 * UserPrivate repeats UserPublic's fields flat (the contract composes them with `allOf`), so
 * the mapping goes through UserPublic rather than duplicating the field list.
 */
private fun UserPrivate.toUser(): User = UserPublic(
    id = id,
    username = username,
    displayName = displayName,
    avatarUrl = avatarUrl,
    bio = bio,
    isOnline = isOnline,
    lastSeenAt = lastSeenAt,
    createdAt = createdAt,
    profileUrl = profileUrl,
    isBlockedByMe = isBlockedByMe,
    profileVisibility = profileVisibility,
    hasBlackBadge = hasBlackBadge,
).toDomain()
